/**
 * Story 059 — View / Edit turbine-model modal (`/turbine-models?model=<uuid>`).
 *
 * R58-CC-02c: the read-only / role-gating UI is delegated to the shared
 * `<ReadOnlyModal>` primitive in `@aeriosiq/ui`. The mode-resolution
 * logic (which combines the role gate with the global/tenant scope axis
 * and the active/inactive axis) stays local in `resolveMode` — but the
 * *result* is fed straight into the primitive via `readOnly` +
 * `bannerOverride`.
 *
 * Mode resolution (UX brief §6 Modal mode matrix):
 *  - tenant_admin × tenant         → Edit  · Save + Deactivate + Cancel
 *  - tenant_admin × global         → View  · Cancel only · banner "This is a global turbine model. Only platform admin can modify." (SB2-CC-05)
 *  - project_manager × tenant      → Edit  · Save + Cancel (NO Deactivate — soft-delete is tenant_admin-only)
 *  - project_manager × global      → View  · Cancel only · banner same as above
 *  - other tenant roles × any      → View  · Cancel only · banner "Read-only — your role does not allow editing this turbine model."
 *  - platform_admin × tenant       → Edit  · Save + Deactivate + Cancel
 *  - platform_admin × global       → Edit  · Save + Deactivate + Cancel
 *  - Inactive row OVERRIDES        → View · Reactivate (only if actor had soft-delete rights on that scope) + Cancel
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ReadOnlyModal, useReadOnlyContext } from "@aeriosiq/ui";
import { useFreshRoles } from "../../sites/shared/useFreshRoles.js";
import {
  resolveModelModalMode,
  type ResolvedModalMode,
} from "../../shared/modelModalMode.js";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import {
  useAssetModelDetailQuery,
  useDeactivateAssetModelMutation,
  useReactivateAssetModelMutation,
  useUpdateAssetModelMutation,
  type AssetModelDetail,
  type AssetModelExternalId,
  type AssetModelSpecification,
  type AssetTypeSummary,
  type UpdateAssetModelBody,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import { ScopeChip } from "../shared/ScopeChip.js";
import { SpecificationsField } from "../shared/SpecificationsField.js";

interface EditTurbineModelFormValues {
  name: string;
  manufacturer: string;
  manufacturerAbbreviation: string;
  assetTypeId: string;
  specifications: AssetModelSpecification[];
  externalIds: AssetModelExternalId[];
  description: string;
}

function detailToFormValues(d: AssetModelDetail): EditTurbineModelFormValues {
  return {
    name: d.name.en ?? "",
    manufacturer: d.manufacturer ?? "",
    manufacturerAbbreviation: d.manufacturerAbbreviation ?? "",
    assetTypeId: d.assetTypeId,
    specifications: d.specifications.map((s) => ({
      parameter: s.parameter,
      unit: s.unit,
      value: s.value,
    })),
    externalIds: d.externalIds.map((e) => ({
      provider: e.provider,
      id: e.id,
    })),
    description: d.description.en ?? "",
  };
}

function dropEmptySpecifications(
  rows: AssetModelSpecification[],
): AssetModelSpecification[] {
  return rows
    .map((r) => ({
      parameter: r.parameter.trim(),
      unit: r.unit.trim(),
      value: r.value.trim(),
    }))
    .filter((r) => r.parameter.length > 0);
}

function dropEmptyExternalIds(
  arr: AssetModelExternalId[],
): AssetModelExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildUpdateBody(v: EditTurbineModelFormValues): UpdateAssetModelBody {
  return {
    name: { en: v.name.trim() },
    manufacturer: v.manufacturer.trim(),
    manufacturerAbbreviation:
      v.manufacturerAbbreviation.trim().length > 0
        ? v.manufacturerAbbreviation.trim()
        : null,
    assetTypeId: v.assetTypeId,
    specifications: dropEmptySpecifications(v.specifications),
    externalIds: dropEmptyExternalIds(v.externalIds),
    description: { en: v.description.trim() },
  };
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}

// Story 074 — the resolver + its result type that used to live here were
// byte-for-byte identical to Story 062's component-model copy. Both now share
// `features/shared/modelModalMode.ts`; the decision matrix documented in this
// file's header is unchanged and lives there too.
type ResolvedMode = ResolvedModalMode;

export interface EditTurbineModelDialogProps {
  modelId: string | null | undefined;
  onClose: () => void;
  assetTypes?: readonly AssetTypeSummary[];
}

export function EditTurbineModelDialog({
  modelId,
  onClose,
  assetTypes = [],
}: EditTurbineModelDialogProps): ReactElement {
  const open = typeof modelId === "string" && modelId.length > 0;
  const {
    data: detail,
    isPending,
    isError,
  } = useAssetModelDetailQuery(modelId);

  const roles = useFreshRoles();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<EditTurbineModelFormValues>({
    defaultValues: {
      name: "",
      manufacturer: "",
      manufacturerAbbreviation: "",
      assetTypeId: "",
      specifications: [],
      externalIds: [],
      description: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (detail) {
      reset(detailToFormValues(detail));
    }
  }, [detail, reset]);

  const update = useUpdateAssetModelMutation();
  const deactivate = useDeactivateAssetModelMutation();
  const reactivate = useReactivateAssetModelMutation();
  const { toast } = useToast();

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);

  const resolved: ResolvedMode = detail
    ? resolveModelModalMode(roles, detail.scope, detail.isActive)
    : {
        mode: "view",
        canSave: false,
        canDeactivate: false,
        canReactivate: false,
        banner: null,
      };
  const readOnly = resolved.mode === "view";
  const isInactive = detail?.isActive === false;

  async function onSubmit(v: EditTurbineModelFormValues): Promise<void> {
    if (!detail) return;
    try {
      await update.mutateAsync({
        id: detail.id,
        body: buildUpdateBody(v),
      });
      toast("Turbine model updated.");
      onClose();
    } catch (err) {
      const errBody = (err as { body?: { code?: string; detail?: string } })
        .body;
      if (errBody?.detail?.includes("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else if (errBody?.code === "forbidden") {
        toast("You don't have permission to edit this model.", "error");
      } else {
        toast("Couldn't save turbine model. Try again.", "error");
      }
    }
  }

  async function doDeactivate(): Promise<void> {
    if (!detail) return;
    try {
      await deactivate.mutateAsync(detail.id);
      toast(`Turbine model ${detail.name.en ?? ""} deactivated.`);
      setConfirmDeactivate(false);
      onClose();
    } catch {
      toast("Couldn't deactivate turbine model.", "error");
    }
  }
  async function doReactivate(): Promise<void> {
    if (!detail) return;
    try {
      await reactivate.mutateAsync(detail.id);
      toast(`Turbine model ${detail.name.en ?? ""} reactivated.`);
      setConfirmReactivate(false);
      onClose();
    } catch {
      toast("Couldn't reactivate turbine model.", "error");
    }
  }

  const title =
    resolved.mode === "edit" ? "Edit turbine model" : "View turbine model";

  const showAudit = detail?.scope === "tenant";

  const subtitle = useMemo(() => {
    if (!detail) return "";
    const scopeLabel = detail.scope === "global" ? "Global" : "Tenant";
    const code =
      typeof detail.assetModelCode === "string"
        ? detail.assetModelCode
        : String(detail.assetModelCode);
    const typeName =
      assetTypes.find((t) => t.id === detail.assetTypeId)?.name ?? "—";
    return `${typeName} · ${scopeLabel} · code ${code}`;
  }, [detail, assetTypes]);

  const turbineCount = detail?.turbineCount ?? 0;
  const turbineCountText =
    turbineCount === 1 ? "1 turbine" : `${String(turbineCount)} turbines`;

  const titleNode = (
    <>
      <Typography
        id="edit-turbine-model-heading"
        component="span"
        variant="h6"
        sx={{ display: "block" }}
      >
        {title}
      </Typography>
      {detail ? (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </>
  );

  // Banner copy resolution — inactive > global-scope > role-deny (default).
  //
  // SB2-CC-05 — the global-scope banner copy was tightened to match
  // Christian's [00:21:23] lock: *"This is a global model. You're not
  // allowed to modify, because the only role who is allowed to modify
  // is the platform admin role."* The copy is "This is a global
  // turbine model. Only platform admin can modify." — distinct from
  // the standard role-deny copy so the *reason* is explicit (scope,
  // not role).
  const bannerOverride = readOnly
    ? isInactive
      ? "This turbine model is inactive. Reactivate to edit."
      : resolved.banner === "global"
        ? "This is a global turbine model. Only platform admin can modify."
        : undefined
    : undefined;

  const actionsNode = detail ? (
    <>
      <Button onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
      {!detail.isActive && resolved.canReactivate ? (
        <Button
          variant="contained"
          onClick={() => {
            setConfirmReactivate(true);
          }}
          disabled={reactivate.isPending}
          startIcon={
            reactivate.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Reactivate
        </Button>
      ) : null}
      {detail.isActive && resolved.canDeactivate ? (
        <Button
          color="error"
          onClick={() => {
            setConfirmDeactivate(true);
          }}
        >
          Deactivate
        </Button>
      ) : null}
      {detail.isActive && resolved.canSave ? (
        <>
          <Box sx={{ flex: 1 }} />
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            disabled={!isDirty || update.isPending}
            startIcon={
              update.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Save changes
          </Button>
        </>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <ReadOnlyModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-turbine-model-heading"
        readOnly={readOnly}
        entityNoun="turbine model"
        {...(bannerOverride ? { bannerOverride } : {})}
        title={titleNode}
        actions={actionsNode}
        showActionsInReadOnly={
          readOnly && !!detail && !detail.isActive && resolved.canReactivate
        }
      >
        {isPending ? (
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : isError ? (
          <Alert severity="error">Couldn&apos;t load this turbine model.</Alert>
        ) : (
          <Stack spacing={2}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Identity
            </Typography>
            <TextField
              label="Model ID"
              value={detail.id}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Model ID",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <TextField
              label="Model code"
              value={
                typeof detail.assetModelCode === "string"
                  ? detail.assetModelCode
                  : String(detail.assetModelCode)
              }
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Model code",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Scope:
              </Typography>
              <ScopeChip scope={detail.scope} />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Details
            </Typography>

            <TextField
              {...register("name", { required: true })}
              label="Name"
              required
              slotProps={{
                htmlInput: {
                  "aria-label": "Name",
                  "aria-required": true,
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <Controller
              name="manufacturer"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  label="Manufacturer"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  required
                  slotProps={{
                    htmlInput: {
                      "aria-label": "Manufacturer",
                      "aria-required": true,
                      maxLength: 100,
                    },
                  }}
                  fullWidth
                />
              )}
            />
            <TextField
              {...register("manufacturerAbbreviation")}
              label="Manufacturer abbreviation"
              slotProps={{
                htmlInput: {
                  "aria-label": "Manufacturer abbreviation",
                  maxLength: 20,
                },
              }}
              fullWidth
            />
            <Controller
              name="assetTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const selected =
                  assetTypes.find((t) => t.id === field.value) ?? null;
                return (
                  // SB2-CC-03 — inline Autocomplete must consume the
                  // ReadOnly context so the popover is suppressed in
                  // read-only mode. We delegate to a small local
                  // component (`AssetTypeInlineSelect`) defined at the
                  // bottom of this file so we can call hooks legally.
                  <AssetTypeInlineSelect
                    assetTypes={assetTypes}
                    selected={selected}
                    onChange={(next) => {
                      field.onChange(next ? next.id : "");
                    }}
                  />
                );
              }}
            />

            <SpecificationsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <ExternalIdsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <TextField
              {...register("description")}
              label="Description (EN)"
              multiline
              rows={3}
              slotProps={{
                htmlInput: {
                  "aria-label": "Description",
                  maxLength: 2000,
                },
              }}
              fullWidth
            />

            {showAudit ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Audit
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Created at"
                    value={formatIso(detail.createdAt)}
                    variant="standard"
                    slotProps={{
                      htmlInput: { readOnly: true, tabIndex: -1 },
                    }}
                    fullWidth
                  />
                  <AuditPersonField
                    label="Created by"
                    person={detail.createdBy}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Updated at"
                    value={formatIso(detail.updatedAt)}
                    variant="standard"
                    slotProps={{
                      htmlInput: { readOnly: true, tabIndex: -1 },
                    }}
                    fullWidth
                  />
                  <AuditPersonField
                    label="Updated by"
                    person={detail.updatedBy}
                  />
                </Stack>
                {detail.deletedAt ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Deleted at"
                      value={formatIso(detail.deletedAt)}
                      variant="standard"
                      slotProps={{
                        htmlInput: { readOnly: true, tabIndex: -1 },
                      }}
                      fullWidth
                    />
                    <AuditPersonField
                      label="Deleted by"
                      person={detail.deletedBy}
                    />
                  </Stack>
                ) : null}
              </>
            ) : null}
          </Stack>
        )}
      </ReadOnlyModal>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Confirm: Deactivate model "${detail?.name.en ?? ""}"?`}
        description={
          <Box>
            This model is referenced by <strong>{turbineCountText}</strong>.
            Deactivating hides it from the default catalog but the turbines that
            currently reference it stay intact. You can{" "}
            <strong>reactivate</strong> it later from the Show inactive view.
          </Box>
        }
        confirmLabel="Confirm"
        danger
        pending={deactivate.isPending}
        onConfirm={() => {
          void doDeactivate();
        }}
        onCancel={() => {
          setConfirmDeactivate(false);
        }}
      />

      <ConfirmDialog
        open={confirmReactivate}
        title={`Confirm: Reactivate model "${detail?.name.en ?? ""}"?`}
        description={
          <Box>
            This model will reappear in the default catalog and become
            selectable when creating new turbines.
          </Box>
        }
        confirmLabel="Confirm"
        pending={reactivate.isPending}
        onConfirm={() => {
          void doReactivate();
        }}
        onCancel={() => {
          setConfirmReactivate(false);
        }}
      />
    </>
  );
}

/**
 * SB2-CC-03 — inline Asset Type select that consumes the `<ReadOnlyModal>`
 * context. Extracted so the `useReadOnlyContext` hook call is at component
 * scope (not inside a `Controller.render` closure).
 */
function AssetTypeInlineSelect({
  assetTypes,
  selected,
  onChange,
}: {
  assetTypes: readonly AssetTypeSummary[];
  selected: AssetTypeSummary | null;
  onChange: (next: AssetTypeSummary | null) => void;
}): ReactElement {
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<AssetTypeSummary>
      options={[...assetTypes]}
      value={selected}
      readOnly={readOnly}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, next) => {
        onChange(next);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label="Type"
            required
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Type",
                "aria-required": true,
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}

function AuditPersonField({
  label,
  person,
}: {
  label: string;
  person: { id: string; displayName: string | null } | null;
}): ReactElement {
  if (person?.displayName == null) {
    return (
      <TextField
        label={label}
        value="Unknown"
        variant="standard"
        slotProps={{
          htmlInput: { readOnly: true, tabIndex: -1 },
          input: { sx: { fontStyle: "italic" } },
        }}
        fullWidth
      />
    );
  }
  return (
    <TextField
      label={label}
      value={person.displayName}
      variant="standard"
      slotProps={{
        htmlInput: { readOnly: true, tabIndex: -1 },
      }}
      fullWidth
    />
  );
}
